import { doc, getDoc, onSnapshot, setDoc, type Unsubscribe } from 'firebase/firestore';

import { db } from '../db';
import { createSubscription } from '../subscription';

const USER_SETTINGS_COLLECTION = 'settings';

/**
 * The settings document is shared with exchange-ui (app and web) — same Firebase project, doc id =
 * Firebase Auth uid. Only the fields this app uses are typed here; writes are partial with
 * `{ merge: true }`, so the fields other apps own are never clobbered.
 */
export interface UserSettings {
  language?: string;
}

const getUserSettingsRef = (userId: string) => doc(db, USER_SETTINGS_COLLECTION, userId);

export async function getUserSettings(userId: string): Promise<UserSettings | null> {
  const snap = await getDoc(getUserSettingsRef(userId));
  if (!snap.exists()) return null;

  return snap.data() as UserSettings;
}

export const subscribeUserSettings = createSubscription<string, UserSettings | null>(
  (userId, onData, onError): Unsubscribe =>
    onSnapshot(
      getUserSettingsRef(userId),
      (snap) => {
        onData(snap.exists() ? (snap.data() as UserSettings) : null);
      },
      onError,
    ),
);

export async function updateUserSettings(userId: string, partial: Partial<UserSettings>): Promise<void> {
  await setDoc(getUserSettingsRef(userId), partial, { merge: true });
}
